import React, { useEffect, useRef, useState, useMemo } from 'react';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSupportingText,
  AdminPanelTitle,
  AdminWidgetTitle,
  AdminMetric,
  AdminMetricDelta,
  AdminMetadataText,
  AdminFilterDropdownButton,
  AdminFilterMenuItem,
  AdminFilterChevron,
  ADMIN_SECTION_OFFSET_CLASS,
  ADMIN_METRIC_PANEL_CLASS,
  ADMIN_CONTENT_PANEL_CLASS,
} from '../shared/adminUx';
import * as echarts from 'echarts';
import australiaGeo from './australiaStatesGeo.json';

const formatMetric = (value, opts) => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number' && opts?.currency) {
    return value.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
  }
  return typeof value === 'number' ? value.toLocaleString() : value;
};

const formatExactDateRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - (days - 1));
  const o = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${start.toLocaleDateString('en-AU', o)} – ${end.toLocaleDateString('en-AU', o)}`;
};

const DATE_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '365 days', value: 365 },
];

const PRIMARY = '#1E40AF';
const SECONDARY = '#6366F1';
const ACCENT = '#059669';
const MUTED = '#94A3B8';

const renderDelta = (change, days) => {
  if (change === null || change === undefined) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      <AdminMetricDelta value={change} />
      <AdminMetadataText className="text-mediumgray">vs previous {days} days</AdminMetadataText>
    </div>
  );
};

const EChartComponent = ({ option, style = { width: '100%', height: '300px' } }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  useEffect(() => {
    if (!chartRef.current || !option) return;
    if (chartInstanceRef.current) chartInstanceRef.current.dispose();
    const chart = echarts.init(chartRef.current);
    chartInstanceRef.current = chart;
    chart.setOption(option);
    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [option]);
  return <div ref={chartRef} style={style} />;
};

const FilterDropdown = ({ value, options, open, onToggle, onSelect }) => (
  <div className="relative z-[60]">
    <AdminFilterDropdownButton
      type="button"
      size="micro"
      onClick={onToggle}
    >
      <span>{DATE_OPTIONS.find((d) => d.value === value)?.label || `${value} days`}</span>
      <AdminFilterChevron open={open} />
    </AdminFilterDropdownButton>
    {open && (
      <div className="flex flex-col rounded-xl py-2 absolute z-50 bg-white text-offblack mt-1 shadow-lg border border-lightgray left-0 min-w-[140px] max-h-[240px] overflow-y-auto">
        {options.map((opt) => (
          <AdminFilterMenuItem
            key={opt.value}
            active={value === opt.value}
            onClick={() => onSelect(opt.value)}
          >
            {opt.label}
          </AdminFilterMenuItem>
        ))}
      </div>
    )}
  </div>
);

function generateSeries(length, base, variance = 0.15) {
  const out = [];
  let v = base;
  for (let i = 0; i < length; i++) {
    v *= 1 + (Math.random() - 0.45) * variance;
    out.push(Math.max(0, Math.round(v)));
  }
  return out;
}

function generateMockBase() {
  const len = 365;
  return {
    labels: Array.from({ length: len }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (len - 1 - i));
      return `${d.getMonth() + 1}/${d.getDate()}`;
    }),
    customerNew: generateSeries(len, 180, 0.12),
    businessNew: generateSeries(len, 32, 0.14),
    postsPublished: generateSeries(len, 95, 0.18),
    messages: generateSeries(len, 4200, 0.1),
    activePosts: generateSeries(len, 8200, 0.08),
    revenue: generateSeries(len, 520, 0.11),
    transactions: generateSeries(len, 210, 0.12),
    failedPay: generateSeries(len, 12, 0.25),
    newSub: generateSeries(len, 45, 0.2),
    cancelSub: generateSeries(len, 18, 0.22),
    moderationLayers: {
      removed: generateSeries(len, 22, 0.2),
      warned: generateSeries(len, 38, 0.18),
      cleared: generateSeries(len, 140, 0.1),
    },
    complianceLayers: {
      passed: generateSeries(len, 210, 0.12),
      review: generateSeries(len, 44, 0.2),
      failed: generateSeries(len, 12, 0.25),
    },
    tickets: generateSeries(len, 58, 0.15),
    ticketTrend: generateSeries(len, 52, 0.08),
    firstResponseSpark: generateSeries(365, 2.4, 0.08),
    resolutionSpark: generateSeries(365, 18, 0.09),
    kpi: {
      customers: { value: 24860, change: 8.4 },
      businesses: { value: 4312, change: 6.8 },
      posts: { value: 3482, change: 6.1 },
      quoteRequests: { value: 1284, change: 12.6 },
      openCases: { value: 126, change: -3.1 },
      revenue: { value: 18420, change: 5.9 },
    },
    kpiSparks: {
      customers: generateSeries(365, 800, 0.05),
      businesses: generateSeries(365, 140, 0.06),
      posts: generateSeries(365, 110, 0.07),
      quoteRequests: generateSeries(365, 42, 0.08),
      openCases: generateSeries(365, 130, 0.04),
      revenue: generateSeries(365, 600, 0.05),
    },
    quoteFunnel: [
      { name: 'Quote Requests Submitted', value: 4280 },
      { name: 'Sent to Businesses', value: 3920 },
      { name: 'Viewed', value: 3180 },
      { name: 'Replied', value: 2150 },
      { name: 'Closed', value: 1640 },
    ],
    stateGrowthMap: [
      { name: 'New South Wales', value: 92 },
      { name: 'Victoria', value: 88 },
      { name: 'Queensland', value: 76 },
      { name: 'Western Australia', value: 62 },
      { name: 'South Australia', value: 48 },
      { name: 'Tasmania', value: 35 },
      { name: 'Northern Territory', value: 28 },
      { name: 'Australian Capital Territory', value: 41 },
    ],
    categoryDemand: [
      { name: 'Plumbing', value: 4200 },
      { name: 'Electrical', value: 3580 },
      { name: 'HVAC', value: 2920 },
      { name: 'Landscaping', value: 2180 },
      { name: 'Cleaning', value: 1950 },
      { name: 'Roofing', value: 1620 },
    ],
    stateQuoteRank: [
      { name: 'NSW', value: 3820 },
      { name: 'VIC', value: 3210 },
      { name: 'QLD', value: 2680 },
      { name: 'WA', value: 1540 },
      { name: 'SA', value: 980 },
    ],
    metroLollipop: [
      { name: 'Sydney', value: 2100 },
      { name: 'Melbourne', value: 1980 },
      { name: 'Brisbane', value: 1420 },
      { name: 'Perth', value: 920 },
      { name: 'Adelaide', value: 680 },
      { name: 'Gold Coast', value: 420 },
    ],
    cityMarkers: [
      { name: 'Sydney', coord: [151.2, -33.87], value: 92 },
      { name: 'Melbourne', coord: [144.96, -37.81], value: 88 },
      { name: 'Brisbane', coord: [153.03, -27.47], value: 76 },
      { name: 'Perth', coord: [115.86, -31.95], value: 62 },
      { name: 'Adelaide', coord: [138.6, -34.93], value: 48 },
    ],
    planMix: { basic: 62, pro: 38 },
    revenueByPlan: { basic: 6820, pro: 11600 },
    firstResponse: { value: 2.4, change: -4.2 },
    resolution: { value: 18.2, change: 2.1 },
    geoCategoryPulse: generateSeries(len, 2100, 0.07),
    geoStatePulse: generateSeries(len, 1950, 0.07),
    geoMetroPulse: generateSeries(len, 1700, 0.08),
    geoMapPulse: generateSeries(len, 75, 0.06),
    planMixPulse: generateSeries(len, 58, 0.05),
    revenuePlanPulse: generateSeries(len, 520, 0.09),
  };
}

const sliceLast = (arr, days) => arr.slice(-days);

function comparePreviousPeriod(series, days) {
  if (!series?.length || days < 1) return 0;
  const cur = series.slice(-days);
  const prev = series.slice(-2 * days, -days);
  if (prev.length < days) return 0;
  const sumCur = cur.reduce((a, b) => a + b, 0);
  const sumPrev = prev.reduce((a, b) => a + b, 0);
  if (!sumPrev) return 0;
  return ((sumCur - sumPrev) / sumPrev) * 100;
}

const SectionTitle = ({ children, controls }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
    <AdminPanelTitle className="text-offblack">{children}</AdminPanelTitle>
    {controls ? <div className="flex flex-wrap gap-2">{controls}</div> : null}
  </div>
);

const CardHeader = ({ title, summary, change, days, dateRangeText, right }) => (
  <div className="py-3 px-4 border-b border-mediumgray flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
    <div>
      <AdminWidgetTitle className="text-offblack !text-sm">{title}</AdminWidgetTitle>
      {summary !== undefined && summary !== null && (
        <AdminMetric size="large" className="text-offblack mt-1 block">
          {summary}
        </AdminMetric>
      )}
      {change !== undefined && change !== null && renderDelta(change, days)}
      <AdminMetadataText className="text-mediumgray mt-1 block">{dateRangeText}</AdminMetadataText>
    </div>
    {right ? <div className="shrink-0">{right}</div> : null}
  </div>
);

const KpiSparkline = ({ data, color }) => {
  const option = useMemo(
    () => ({
      backgroundColor: 'transparent',
      grid: { left: 0, right: 0, top: 2, bottom: 0 },
      xAxis: { type: 'category', show: false, data: data.map((_, i) => i) },
      yAxis: { type: 'value', show: false },
      series: [
        {
          type: 'line',
          data,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 1.5, color: color || PRIMARY },
          areaStyle: { color: `${color || PRIMARY}18` },
        },
      ],
    }),
    [data, color]
  );
  return <EChartComponent option={option} style={{ width: '100%', height: 40 }} />;
};

const PlatformAnalytics = () => {
  const [mapReady, setMapReady] = useState(false);
  const [kpiDays, setKpiDays] = useState(30);
  const [opsServiceDays, setOpsServiceDays] = useState(30);
  const [cardDays, setCardDays] = useState({
    customerGrowth: 30,
    businessGrowth: 30,
    publishing: 30,
    messaging: 30,
    quoteFunnel: 30,
    liveSupply: 30,
    auMap: 30,
    categoryDemand: 30,
    stateDemand: 30,
    metroDemand: 30,
    moderation: 30,
    compliance: 30,
    supportTickets: 30,
    revenueTrend: 30,
    transactionFlow: 30,
    planMix: 30,
    revenueByPlan: 30,
    failedPayments: 30,
    subscriptionMovement: 30,
  });
  const [openDd, setOpenDd] = useState(null);

  useEffect(() => {
    echarts.registerMap('australiaGrowth', australiaGeo);
    setMapReady(true);
  }, []);

  const base = useMemo(() => generateMockBase(), []);

  const setDay = (key, v) => setCardDays((d) => ({ ...d, [key]: v }));

  const dateDropdown = (key, valueKey) => (
    <FilterDropdown
      value={cardDays[valueKey]}
      options={DATE_OPTIONS}
      open={openDd === key}
      onToggle={() => setOpenDd((k) => (k === key ? null : key))}
      onSelect={(v) => {
        setDay(valueKey, v);
        setOpenDd(null);
      }}
    />
  );

  const customerGrowthOpt = useMemo(() => {
    const days = cardDays.customerGrowth;
    const lab = sliceLast(base.labels, days);
    const ser = sliceLast(base.customerNew, days);
    const change = comparePreviousPeriod(base.customerNew, days);
    return {
      option: {
        backgroundColor: 'transparent',
        tooltip: { trigger: 'axis' },
        grid: { top: 28, right: 16, bottom: 28, left: 48 },
        xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10, color: MUTED } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
        series: [{ type: 'line', data: ser, smooth: true, areaStyle: { color: `${PRIMARY}14` }, lineStyle: { color: PRIMARY } }],
      },
      change,
    };
  }, [base, cardDays.customerGrowth]);

  const businessGrowthOpt = useMemo(() => {
    const days = cardDays.businessGrowth;
    const lab = sliceLast(base.labels, days);
    const ser = sliceLast(base.businessNew, days);
    const change = comparePreviousPeriod(base.businessNew, days);
    return {
      option: {
        backgroundColor: 'transparent',
        tooltip: { trigger: 'axis' },
        grid: { top: 28, right: 16, bottom: 28, left: 48 },
        xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10, color: MUTED } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
        series: [{ type: 'line', data: ser, smooth: true, areaStyle: { color: `${ACCENT}14` }, lineStyle: { color: ACCENT } }],
      },
      change,
    };
  }, [base, cardDays.businessGrowth]);

  const publishingOpt = useMemo(() => {
    const days = cardDays.publishing;
    const lab = sliceLast(base.labels, days);
    const ser = sliceLast(base.postsPublished, days);
    const change = comparePreviousPeriod(base.postsPublished, days);
    return {
      option: {
        backgroundColor: 'transparent',
        tooltip: { trigger: 'axis' },
        grid: { top: 28, right: 16, bottom: 28, left: 48 },
        xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10, color: MUTED } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
        series: [{ type: 'bar', data: ser, itemStyle: { color: SECONDARY }, barMaxWidth: 22 }],
      },
      change,
    };
  }, [base, cardDays.publishing]);

  const messagingOpt = useMemo(() => {
    const days = cardDays.messaging;
    const lab = sliceLast(base.labels, days);
    const ser = sliceLast(base.messages, days);
    const change = comparePreviousPeriod(base.messages, days);
    return {
      option: {
        backgroundColor: 'transparent',
        tooltip: { trigger: 'axis' },
        grid: { top: 28, right: 16, bottom: 28, left: 52 },
        xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10, color: MUTED } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
        series: [
          {
            type: 'line',
            data: ser,
            smooth: true,
            areaStyle: { color: `${PRIMARY}22` },
            lineStyle: { color: PRIMARY },
          },
        ],
      },
      change,
    };
  }, [base, cardDays.messaging]);

  const quoteFunnelOpt = useMemo(() => {
    const days = cardDays.quoteFunnel;
    const scale = 0.88 + (Math.min(days, 365) / 365) * 0.24;
    const d = base.quoteFunnel.map((step, i) => ({
      ...step,
      value: Math.max(1, Math.round(step.value * scale * (1 - i * 0.015))),
    }));
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', formatter: '{b}: {c}' },
      series: [
        {
          type: 'funnel',
          left: '8%',
          top: 24,
          bottom: 24,
          width: '84%',
          min: 0,
          max: d[0].value,
          sort: 'descending',
          gap: 6,
          label: { show: true, position: 'inside', fontSize: 11 },
          data: d,
          itemStyle: { borderColor: '#fff', borderWidth: 1 },
          color: [PRIMARY, '#3B5CB8', '#5B73C6', '#7A8DD4', '#99A7E2'],
        },
      ],
    };
  }, [base.quoteFunnel, cardDays.quoteFunnel]);

  const liveSupplyOpt = useMemo(() => {
    const days = cardDays.liveSupply;
    const lab = sliceLast(base.labels, days);
    const ser = sliceLast(base.activePosts, days);
    const change = comparePreviousPeriod(base.activePosts, days);
    return {
      option: {
        backgroundColor: 'transparent',
        tooltip: { trigger: 'axis' },
        grid: { top: 28, right: 16, bottom: 28, left: 52 },
        xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10, color: MUTED } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
        series: [{ type: 'line', data: ser, step: 'end', lineStyle: { color: PRIMARY, width: 2 }, symbol: 'none' }],
      },
      change,
    };
  }, [base, cardDays.liveSupply]);

  const australiaMapOpt = useMemo(() => {
    if (!mapReady) return null;
    const f = 0.9 + (Math.min(cardDays.auMap, 365) / 365) * 0.2;
    const data = base.stateGrowthMap.map((s) => ({ ...s, value: Math.min(100, Math.round(s.value * f)) }));
    const cityMarkersScaled = base.cityMarkers.map((c) => ({
      ...c,
      value: Math.min(100, Math.round(c.value * f)),
    }));
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', formatter: '{b}: {c}' },
      geo: {
        map: 'australiaGrowth',
        roam: false,
        zoom: 1.05,
        label: { show: false },
        itemStyle: { areaColor: '#e8eef9', borderColor: '#fff' },
        emphasis: { label: { show: true, fontSize: 10 }, itemStyle: { areaColor: PRIMARY } },
      },
      visualMap: {
        min: 0,
        max: 100,
        left: 16,
        bottom: 24,
        text: ['High', 'Low'],
        calculable: true,
        seriesIndex: 0,
        inRange: { color: ['#dbeafe', '#1e3a8a'] },
      },
      series: [
        {
          name: 'Growth',
          type: 'map',
          map: 'australiaGrowth',
          geoIndex: 0,
          data,
        },
        {
          name: 'Cities',
          type: 'scatter',
          coordinateSystem: 'geo',
          geoIndex: 0,
          data: cityMarkersScaled.map((c) => ({ name: c.name, value: [...c.coord, c.value] })),
          symbolSize: (raw) => 8 + (Array.isArray(raw) ? raw[2] : raw?.value?.[2] || 0) / 100 * 12,
          itemStyle: { color: '#f59e0b', borderColor: '#fff', borderWidth: 1 },
          label: { show: true, formatter: '{b}', position: 'top', fontSize: 9, color: '#334155' },
        },
      ],
    };
  }, [base, mapReady, cardDays.auMap]);

  const categoryBarsOpt = useMemo(() => {
    const f = 0.94 + (Math.min(cardDays.categoryDemand, 365) / 365) * 0.12;
    const scaled = base.categoryDemand.map((x) => ({ ...x, value: Math.round(x.value * f) }));
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { top: 8, right: 24, bottom: 8, left: 100 },
      xAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
      yAxis: { type: 'category', data: scaled.map((x) => x.name).reverse(), axisLabel: { fontSize: 10 } },
      series: [
        {
          type: 'bar',
          data: scaled.map((x) => x.value).reverse(),
          itemStyle: { color: PRIMARY },
          barMaxWidth: 16,
        },
      ],
    };
  }, [base.categoryDemand, cardDays.categoryDemand]);

  const stateQuoteBarsOpt = useMemo(() => {
    const f = 0.93 + (Math.min(cardDays.stateDemand, 365) / 365) * 0.14;
    const scaled = base.stateQuoteRank.map((x) => ({ ...x, value: Math.round(x.value * f) }));
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { top: 8, right: 24, bottom: 8, left: 56 },
      xAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
      yAxis: { type: 'category', data: scaled.map((x) => x.name).reverse(), axisLabel: { fontSize: 10 } },
      series: [{ type: 'bar', data: scaled.map((x) => x.value).reverse(), itemStyle: { color: SECONDARY }, barMaxWidth: 16 }],
    };
  }, [base.stateQuoteRank, cardDays.stateDemand]);

  const metroLollipopOpt = useMemo(() => {
    const f = 0.92 + (Math.min(cardDays.metroDemand, 365) / 365) * 0.16;
    const scaled = base.metroLollipop.map((x) => ({ ...x, value: Math.round(x.value * f) }));
    const cities = scaled.map((x) => x.name);
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', formatter: '{b}: {c}' },
      grid: { top: 24, right: 24, bottom: 32, left: 88 },
      xAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
      yAxis: { type: 'category', data: cities, axisLabel: { fontSize: 10 } },
      series: [
        {
          type: 'scatter',
          symbolSize: 14,
          data: scaled.map((x, i) => [x.value, i]),
          itemStyle: { color: ACCENT, borderColor: '#fff', borderWidth: 1 },
        },
      ],
    };
  }, [base.metroLollipop, cardDays.metroDemand]);

  const moderationOpt = useMemo(() => {
    const days = cardDays.moderation;
    const lab = sliceLast(base.labels, days);
    const r = sliceLast(base.moderationLayers.removed, days);
    const w = sliceLast(base.moderationLayers.warned, days);
    const c = sliceLast(base.moderationLayers.cleared, days);
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis' },
      legend: { data: ['Removed', 'Warned', 'Cleared'], top: 8 },
      grid: { top: 44, right: 16, bottom: 28, left: 48 },
      xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10 } },
      yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
      series: [
        { name: 'Removed', type: 'bar', stack: 'm', data: r, itemStyle: { color: '#fecaca' } },
        { name: 'Warned', type: 'bar', stack: 'm', data: w, itemStyle: { color: '#fde68a' } },
        { name: 'Cleared', type: 'bar', stack: 'm', data: c, itemStyle: { color: '#bbf7d0' } },
      ],
    };
  }, [base, cardDays.moderation]);

  const complianceOpt = useMemo(() => {
    const days = cardDays.compliance;
    const lab = sliceLast(base.labels, days);
    const p = sliceLast(base.complianceLayers.passed, days);
    const rv = sliceLast(base.complianceLayers.review, days);
    const f = sliceLast(base.complianceLayers.failed, days);
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis' },
      legend: { data: ['Passed', 'Review', 'Failed'], top: 8 },
      grid: { top: 44, right: 16, bottom: 28, left: 48 },
      xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10 } },
      yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
      series: [
        { name: 'Passed', type: 'bar', stack: 'c', data: p, itemStyle: { color: ACCENT } },
        { name: 'Review', type: 'bar', stack: 'c', data: rv, itemStyle: { color: '#fbbf24' } },
        { name: 'Failed', type: 'bar', stack: 'c', data: f, itemStyle: { color: '#ef4444' } },
      ],
    };
  }, [base, cardDays.compliance]);

  const supportTicketsOpt = useMemo(() => {
    const days = cardDays.supportTickets;
    const lab = sliceLast(base.labels, days);
    const vol = sliceLast(base.tickets, days);
    const trend = sliceLast(base.ticketTrend, days);
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis' },
      legend: { data: ['Volume', 'Trend'], top: 8 },
      grid: { top: 44, right: 48, bottom: 28, left: 48 },
      xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10 } },
      yAxis: [
        { type: 'value', name: 'Tickets', splitLine: { lineStyle: { color: '#f1f5f9' } } },
        { type: 'value', name: 'Trend', splitLine: { show: false } },
      ],
      series: [
        { name: 'Volume', type: 'bar', data: vol, itemStyle: { color: `${PRIMARY}99` }, barMaxWidth: 20 },
        { name: 'Trend', type: 'line', yAxisIndex: 1, data: trend, smooth: true, lineStyle: { color: '#f59e0b', width: 2 } },
      ],
    };
  }, [base, cardDays.supportTickets]);

  const revenueTrendOpt = useMemo(() => {
    const days = cardDays.revenueTrend;
    const lab = sliceLast(base.labels, days);
    const ser = sliceLast(base.revenue, days);
    const change = comparePreviousPeriod(base.revenue, days);
    return {
      option: {
        backgroundColor: 'transparent',
        tooltip: { trigger: 'axis', formatter: (p) => `${p[0].axisValue}<br/>${formatMetric(p[0].value, { currency: true })}` },
        grid: { top: 28, right: 16, bottom: 28, left: 56 },
        xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10 } },
        yAxis: { type: 'value', axisLabel: { formatter: (v) => `$${v}` }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
        series: [{ type: 'line', data: ser, smooth: true, areaStyle: { color: `${ACCENT}22` }, lineStyle: { color: ACCENT } }],
      },
      change,
    };
  }, [base, cardDays.revenueTrend]);

  const transactionFlowOpt = useMemo(() => {
    const days = cardDays.transactionFlow;
    const lab = sliceLast(base.labels, days);
    const tx = sliceLast(base.transactions, days);
    const val = sliceLast(base.revenue, days);
    const change = comparePreviousPeriod(base.transactions, days);
    return {
      option: {
        backgroundColor: 'transparent',
        tooltip: { trigger: 'axis' },
        legend: { data: ['Transactions', 'Value (AUD)'], top: 8 },
        grid: { top: 44, right: 52, bottom: 28, left: 52 },
        xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10 } },
        yAxis: [
          { type: 'value', name: 'Count' },
          { type: 'value', name: 'AUD', axisLabel: { formatter: (v) => `$${v}` } },
        ],
        series: [
          { name: 'Transactions', type: 'bar', data: tx, itemStyle: { color: PRIMARY }, barMaxWidth: 14 },
          { name: 'Value (AUD)', type: 'line', yAxisIndex: 1, data: val, smooth: true, lineStyle: { color: '#f59e0b' } },
        ],
      },
      change,
    };
  }, [base, cardDays.transactionFlow]);

  const planMixOpt = useMemo(
    () => ({
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { data: ['Basic', 'Pro'], top: 8 },
      grid: { top: 40, right: 16, bottom: 28, left: 48 },
      xAxis: { type: 'category', data: ['Share'] },
      yAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' } },
      series: [
        { name: 'Basic', type: 'bar', stack: 'p', data: [base.planMix.basic], itemStyle: { color: '#93c5fd' } },
        { name: 'Pro', type: 'bar', stack: 'p', data: [base.planMix.pro], itemStyle: { color: PRIMARY } },
      ],
    }),
    [base.planMix]
  );

  const revenueByPlanOpt = useMemo(
    () => ({
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { data: ['Basic revenue', 'Pro revenue'], top: 8 },
      grid: { top: 40, right: 24, bottom: 28, left: 56 },
      xAxis: { type: 'value', axisLabel: { formatter: (v) => `$${v / 1000}k` } },
      yAxis: { type: 'category', data: ['Plans'], axisLabel: { fontSize: 10 } },
      series: [
        { name: 'Basic revenue', type: 'bar', stack: 'r', data: [base.revenueByPlan.basic], itemStyle: { color: '#93c5fd' } },
        { name: 'Pro revenue', type: 'bar', stack: 'r', data: [base.revenueByPlan.pro], itemStyle: { color: PRIMARY } },
      ],
    }),
    [base.revenueByPlan]
  );

  const failedPaymentsOpt = useMemo(() => {
    const days = cardDays.failedPayments;
    const lab = sliceLast(base.labels, days);
    const ser = sliceLast(base.failedPay, days);
    const change = comparePreviousPeriod(base.failedPay, days);
    return {
      option: {
        backgroundColor: 'transparent',
        tooltip: { trigger: 'axis' },
        grid: { top: 28, right: 16, bottom: 28, left: 48 },
        xAxis: { type: 'category', data: lab, axisLabel: { fontSize: 10 } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
        series: [{ type: 'bar', data: ser, itemStyle: { color: '#ef4444' }, barMaxWidth: 18 }],
      },
      change,
    };
  }, [base, cardDays.failedPayments]);

  const subscriptionMovementOpt = useMemo(() => {
    const days = cardDays.subscriptionMovement;
    const lab = sliceLast(base.labels, days);
    const ns = sliceLast(base.newSub, days);
    const cs = sliceLast(base.cancelSub, days);
    const bucket = Math.max(1, Math.floor(days / 12));
    const labels = [];
    const newSums = [];
    const cancelSums = [];
    for (let i = 0; i < lab.length; i += bucket) {
      labels.push(lab[i]);
      newSums.push(ns.slice(i, i + bucket).reduce((a, b) => a + b, 0));
      cancelSums.push(cs.slice(i, i + bucket).reduce((a, b) => a + b, 0));
    }
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'axis' },
      legend: { data: ['New', 'Cancellations'], top: 8 },
      grid: { top: 44, right: 16, bottom: 28, left: 48 },
      xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10 } },
      yAxis: { type: 'value', splitLine: { lineStyle: { color: '#f1f5f9' } } },
      series: [
        { name: 'New', type: 'bar', data: newSums, itemStyle: { color: ACCENT }, barMaxWidth: 18 },
        { name: 'Cancellations', type: 'bar', data: cancelSums, itemStyle: { color: '#f87171' }, barMaxWidth: 18 },
      ],
    };
  }, [base, cardDays.subscriptionMovement]);

  const moderationDaily = useMemo(
    () =>
      base.moderationLayers.removed.map(
        (_, i) =>
          base.moderationLayers.removed[i] + base.moderationLayers.warned[i] + base.moderationLayers.cleared[i]
      ),
    [base]
  );

  const complianceDaily = useMemo(
    () =>
      base.complianceLayers.passed.map(
        (_, i) =>
          base.complianceLayers.passed[i] + base.complianceLayers.review[i] + base.complianceLayers.failed[i]
      ),
    [base]
  );

  const moderationPop = useMemo(
    () => comparePreviousPeriod(moderationDaily, cardDays.moderation),
    [moderationDaily, cardDays.moderation]
  );
  const compliancePop = useMemo(
    () => comparePreviousPeriod(complianceDaily, cardDays.compliance),
    [complianceDaily, cardDays.compliance]
  );
  const supportPop = useMemo(
    () => comparePreviousPeriod(base.tickets, cardDays.supportTickets),
    [base.tickets, cardDays.supportTickets]
  );

  const funnelTopStats = useMemo(() => {
    const days = cardDays.quoteFunnel;
    const scale = 0.88 + (Math.min(days, 365) / 365) * 0.24;
    const top = Math.max(1, Math.round(base.quoteFunnel[0].value * scale));
    const change = comparePreviousPeriod(base.kpiSparks.quoteRequests, days);
    return { topStr: top.toLocaleString(), change };
  }, [base, cardDays.quoteFunnel]);

  const geoCatPop = useMemo(
    () => comparePreviousPeriod(base.geoCategoryPulse, cardDays.categoryDemand),
    [base.geoCategoryPulse, cardDays.categoryDemand]
  );
  const geoStatePop = useMemo(
    () => comparePreviousPeriod(base.geoStatePulse, cardDays.stateDemand),
    [base.geoStatePulse, cardDays.stateDemand]
  );
  const geoMetroPop = useMemo(
    () => comparePreviousPeriod(base.geoMetroPulse, cardDays.metroDemand),
    [base.geoMetroPulse, cardDays.metroDemand]
  );
  const geoMapPop = useMemo(
    () => comparePreviousPeriod(base.geoMapPulse, cardDays.auMap),
    [base.geoMapPulse, cardDays.auMap]
  );

  const planMixPop = useMemo(
    () => comparePreviousPeriod(base.planMixPulse, cardDays.planMix),
    [base.planMixPulse, cardDays.planMix]
  );
  const revenueByPlanPop = useMemo(
    () => comparePreviousPeriod(base.revenuePlanPulse, cardDays.revenueByPlan),
    [base.revenuePlanPulse, cardDays.revenueByPlan]
  );
  const subscriptionPop = useMemo(
    () => comparePreviousPeriod(base.newSub, cardDays.subscriptionMovement),
    [base.newSub, cardDays.subscriptionMovement]
  );

  const kpiPop = useMemo(
    () => ({
      customers: comparePreviousPeriod(base.kpiSparks.customers, kpiDays),
      businesses: comparePreviousPeriod(base.kpiSparks.businesses, kpiDays),
      posts: comparePreviousPeriod(base.kpiSparks.posts, kpiDays),
      quoteRequests: comparePreviousPeriod(base.kpiSparks.quoteRequests, kpiDays),
      openCases: comparePreviousPeriod(base.kpiSparks.openCases, kpiDays),
      revenue: comparePreviousPeriod(base.kpiSparks.revenue, kpiDays),
    }),
    [base, kpiDays]
  );

  const revenuePeriodLabel = useMemo(() => {
    const s = sliceLast(base.revenue, cardDays.revenueTrend);
    const total = s.reduce((a, b) => a + b, 0);
    return formatMetric(Math.round(total * 28.4), { currency: true });
  }, [base, cardDays.revenueTrend]);

  const kpiDateText = formatExactDateRange(kpiDays);
  const kpi = base.kpi;

  return (
    <AdminLayout>
      {openDd && (
        <div
          className="fixed inset-0 z-[40] bg-transparent"
          aria-hidden
          onClick={() => setOpenDd(null)}
        />
      )}
      <div>
        <AdminPageTitle>Analytics</AdminPageTitle>

        <section className={ADMIN_SECTION_OFFSET_CLASS}>
          <SectionTitle
            controls={
              <FilterDropdown
                value={kpiDays}
                options={DATE_OPTIONS}
                open={openDd === 'kpi'}
                onToggle={() => setOpenDd((k) => (k === 'kpi' ? null : 'kpi'))}
                onSelect={(v) => {
                  setKpiDays(v);
                  setOpenDd(null);
                }}
              />
            }
          >
            KPI Summary
          </SectionTitle>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {[
              { label: 'Customers', k: 'customers', spark: 'customers', currency: false },
              { label: 'Businesses', k: 'businesses', spark: 'businesses', currency: false },
              { label: 'Posts', k: 'posts', spark: 'posts', currency: false },
              { label: 'Quote Requests', k: 'quoteRequests', spark: 'quoteRequests', currency: false },
              { label: 'Open Operational Cases', k: 'openCases', spark: 'openCases', currency: false },
              { label: 'Revenue', k: 'revenue', spark: 'revenue', currency: true },
            ].map((row) => (
              <DashboardMainContainer key={row.label} className={`${ADMIN_METRIC_PANEL_CLASS} p-4 flex flex-col min-h-[148px]`}>
                <AdminSupportingText className="text-offblack opacity-80">{row.label}</AdminSupportingText>
                <AdminMetric size="large" className="text-offblack mt-1 block">
                  {formatMetric(kpi[row.k].value, { currency: row.currency })}
                </AdminMetric>
                {renderDelta(kpiPop[row.k], kpiDays)}
                <AdminMetadataText className="text-mediumgray mt-2 leading-snug block">{kpiDateText}</AdminMetadataText>
                <div className="mt-auto pt-2">
                  <KpiSparkline data={sliceLast(base.kpiSparks[row.spark], kpiDays)} color={PRIMARY} />
                </div>
              </DashboardMainContainer>
            ))}
          </div>
        </section>

        <section className={ADMIN_SECTION_OFFSET_CLASS}>
          <SectionTitle>Marketplace Movement</SectionTitle>

          <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} ${ADMIN_SECTION_OFFSET_CLASS}`}>
            <CardHeader
              title="Quote Conversion Funnel"
              summary={`${funnelTopStats.topStr} quote requests`}
              change={funnelTopStats.change}
              days={cardDays.quoteFunnel}
              dateRangeText={formatExactDateRange(cardDays.quoteFunnel)}
              right={dateDropdown('mf-funnel', 'quoteFunnel')}
            />
            <div className="p-4">
              <EChartComponent option={quoteFunnelOpt} style={{ width: '100%', height: 360 }} />
            </div>
          </DashboardMainContainer>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Customer Growth"
                summary="New customers"
                change={customerGrowthOpt.change}
                days={cardDays.customerGrowth}
                dateRangeText={formatExactDateRange(cardDays.customerGrowth)}
                right={dateDropdown('mf-cust', 'customerGrowth')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={customerGrowthOpt.option} style={{ width: '100%', height: 280 }} />
              </div>
            </DashboardMainContainer>
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Business Growth"
                summary="New businesses"
                change={businessGrowthOpt.change}
                days={cardDays.businessGrowth}
                dateRangeText={formatExactDateRange(cardDays.businessGrowth)}
                right={dateDropdown('mf-biz', 'businessGrowth')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={businessGrowthOpt.option} style={{ width: '100%', height: 280 }} />
              </div>
            </DashboardMainContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Publishing Activity"
                summary="Posts published"
                change={publishingOpt.change}
                days={cardDays.publishing}
                dateRangeText={formatExactDateRange(cardDays.publishing)}
                right={dateDropdown('mf-pub', 'publishing')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={publishingOpt.option} style={{ width: '100%', height: 280 }} />
              </div>
            </DashboardMainContainer>
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Messaging Activity"
                summary="Messages sent"
                change={messagingOpt.change}
                days={cardDays.messaging}
                dateRangeText={formatExactDateRange(cardDays.messaging)}
                right={dateDropdown('mf-msg', 'messaging')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={messagingOpt.option} style={{ width: '100%', height: 280 }} />
              </div>
            </DashboardMainContainer>
          </div>

          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <CardHeader
              title="Live Supply Trend"
              summary="Active posts"
              change={liveSupplyOpt.change}
              days={cardDays.liveSupply}
              dateRangeText={formatExactDateRange(cardDays.liveSupply)}
              right={dateDropdown('mf-supply', 'liveSupply')}
            />
            <div className="p-4">
              <EChartComponent option={liveSupplyOpt.option} style={{ width: '100%', height: 300 }} />
            </div>
          </DashboardMainContainer>
        </section>

        <section className={ADMIN_SECTION_OFFSET_CLASS}>
          <SectionTitle>Geography & Demand</SectionTitle>
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} xl:col-span-7 min-h-[420px]`}>
              <CardHeader
                title="Australia Growth Map"
                summary="State growth intensity & city markers"
                change={geoMapPop}
                days={cardDays.auMap}
                dateRangeText={formatExactDateRange(cardDays.auMap)}
                right={dateDropdown('geo-map', 'auMap')}
              />
              <div className="p-4 flex-1 min-h-[340px]">{australiaMapOpt && <EChartComponent option={australiaMapOpt} style={{ width: '100%', height: 360 }} />}</div>
            </DashboardMainContainer>
            <div className="xl:col-span-5 flex flex-col gap-6">
              <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} flex-1`}>
                <CardHeader
                  title="Category Demand Leaders"
                  summary="Strongest categories"
                  change={geoCatPop}
                  days={cardDays.categoryDemand}
                  dateRangeText={formatExactDateRange(cardDays.categoryDemand)}
                  right={dateDropdown('geo-cat', 'categoryDemand')}
                />
                <div className="p-4 flex-1">
                  <EChartComponent option={categoryBarsOpt} style={{ width: '100%', height: 260 }} />
                </div>
              </DashboardMainContainer>
              <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} flex-1`}>
                <CardHeader
                  title="State Demand Ranking"
                  summary="Quote requests by state"
                  change={geoStatePop}
                  days={cardDays.stateDemand}
                  dateRangeText={formatExactDateRange(cardDays.stateDemand)}
                  right={dateDropdown('geo-state', 'stateDemand')}
                />
                <div className="p-4 flex-1">
                  <EChartComponent option={stateQuoteBarsOpt} style={{ width: '100%', height: 220 }} />
                </div>
              </DashboardMainContainer>
            </div>
          </div>
          <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} ${ADMIN_SECTION_OFFSET_CLASS}`}>
            <CardHeader
              title="Metro Demand Comparison"
              summary="City-level demand"
              change={geoMetroPop}
              days={cardDays.metroDemand}
              dateRangeText={formatExactDateRange(cardDays.metroDemand)}
              right={dateDropdown('geo-metro', 'metroDemand')}
            />
            <div className="p-4">
              <EChartComponent option={metroLollipopOpt} style={{ width: '100%', height: 300 }} />
            </div>
          </DashboardMainContainer>
        </section>

        <section className={ADMIN_SECTION_OFFSET_CLASS}>
          <SectionTitle
            controls={
              <FilterDropdown
                value={opsServiceDays}
                options={DATE_OPTIONS}
                open={openDd === 'ops'}
                onToggle={() => setOpenDd((k) => (k === 'ops' ? null : 'ops'))}
                onSelect={(v) => {
                  setOpsServiceDays(v);
                  setOpenDd(null);
                }}
              />
            }
          >
            Operations & Service Performance
          </SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Moderation Outcomes"
                summary="Closed moderation mix"
                change={moderationPop}
                days={cardDays.moderation}
                dateRangeText={formatExactDateRange(cardDays.moderation)}
                right={dateDropdown('op-mod', 'moderation')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={moderationOpt} style={{ width: '100%', height: 280 }} />
              </div>
            </DashboardMainContainer>
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Compliance Pipeline"
                summary="Checks & outcomes over time"
                change={compliancePop}
                days={cardDays.compliance}
                dateRangeText={formatExactDateRange(cardDays.compliance)}
                right={dateDropdown('op-comp', 'compliance')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={complianceOpt} style={{ width: '100%', height: 280 }} />
              </div>
            </DashboardMainContainer>
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Support Ticket Load"
                summary="Volume with trend overlay"
                change={supportPop}
                days={cardDays.supportTickets}
                dateRangeText={formatExactDateRange(cardDays.supportTickets)}
                right={dateDropdown('op-tix', 'supportTickets')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={supportTicketsOpt} style={{ width: '100%', height: 280 }} />
              </div>
            </DashboardMainContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DashboardMainContainer className="p-4">
              <AdminSupportingText className="text-offblack opacity-80">First Response Time</AdminSupportingText>
              <AdminMetric size="large" className="text-offblack mt-1 block">
                {base.firstResponse.value} hrs avg
              </AdminMetric>
              {renderDelta(comparePreviousPeriod(base.firstResponseSpark, opsServiceDays), opsServiceDays)}
              <AdminMetadataText className="text-mediumgray mt-2 block">{formatExactDateRange(opsServiceDays)}</AdminMetadataText>
              <div className="mt-3">
                <KpiSparkline data={sliceLast(base.firstResponseSpark, opsServiceDays)} color={PRIMARY} />
              </div>
            </DashboardMainContainer>
            <DashboardMainContainer className="p-4">
              <AdminSupportingText className="text-offblack opacity-80">Resolution Time</AdminSupportingText>
              <AdminMetric size="large" className="text-offblack mt-1 block">
                {base.resolution.value} hrs avg
              </AdminMetric>
              {renderDelta(comparePreviousPeriod(base.resolutionSpark, opsServiceDays), opsServiceDays)}
              <AdminMetadataText className="text-mediumgray mt-2 block">{formatExactDateRange(opsServiceDays)}</AdminMetadataText>
              <div className="mt-3">
                <KpiSparkline data={sliceLast(base.resolutionSpark, opsServiceDays)} color={ACCENT} />
              </div>
            </DashboardMainContainer>
          </div>
        </section>

        <section className="pb-8">
          <SectionTitle>Revenue & Payments</SectionTitle>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Revenue Trend"
                summary={revenuePeriodLabel}
                change={revenueTrendOpt.change}
                days={cardDays.revenueTrend}
                dateRangeText={formatExactDateRange(cardDays.revenueTrend)}
                right={dateDropdown('rev-trend', 'revenueTrend')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={revenueTrendOpt.option} style={{ width: '100%', height: 300 }} />
              </div>
            </DashboardMainContainer>
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Transaction Flow"
                summary="Count & payment value"
                change={transactionFlowOpt.change}
                days={cardDays.transactionFlow}
                dateRangeText={formatExactDateRange(cardDays.transactionFlow)}
                right={dateDropdown('rev-tx', 'transactionFlow')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={transactionFlowOpt.option} style={{ width: '100%', height: 300 }} />
              </div>
            </DashboardMainContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Plan Mix"
                summary="Subscription composition"
                change={planMixPop}
                days={cardDays.planMix}
                dateRangeText={formatExactDateRange(cardDays.planMix)}
                right={dateDropdown('rev-mix', 'planMix')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={planMixOpt} style={{ width: '100%', height: 220 }} />
              </div>
            </DashboardMainContainer>
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Revenue by Plan"
                summary="Basic vs Pro"
                change={revenueByPlanPop}
                days={cardDays.revenueByPlan}
                dateRangeText={formatExactDateRange(cardDays.revenueByPlan)}
                right={dateDropdown('rev-planbar', 'revenueByPlan')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={revenueByPlanOpt} style={{ width: '100%', height: 220 }} />
              </div>
            </DashboardMainContainer>
            <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
              <CardHeader
                title="Failed Payments"
                summary="Volume trend"
                change={failedPaymentsOpt.change}
                days={cardDays.failedPayments}
                dateRangeText={formatExactDateRange(cardDays.failedPayments)}
                right={dateDropdown('rev-fail', 'failedPayments')}
              />
              <div className="p-4 flex-1">
                <EChartComponent option={failedPaymentsOpt.option} style={{ width: '100%', height: 220 }} />
              </div>
            </DashboardMainContainer>
          </div>

          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <CardHeader
              title="Subscription Movement"
              summary="New vs cancellations"
              change={subscriptionPop}
              days={cardDays.subscriptionMovement}
              dateRangeText={formatExactDateRange(cardDays.subscriptionMovement)}
              right={dateDropdown('rev-sub', 'subscriptionMovement')}
            />
            <div className="p-4">
              <EChartComponent option={subscriptionMovementOpt} style={{ width: '100%', height: 300 }} />
            </div>
          </DashboardMainContainer>
        </section>
      </div>
    </AdminLayout>
  );
};

export default PlatformAnalytics;
